import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { rupees, formatIndian, purityColor } from "@/lib/utils";
import { Package, Store, Calendar, TrendingUp, Search, Sparkles, BadgeCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import MemoBadge from "@/components/MemoBadge";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [memoSummary, setMemoSummary] = useState(null);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
    api.get("/dashboard/memo-summary").then((r) => setMemoSummary(r.data)).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    navigate(`/products?search=${encodeURIComponent(search.trim())}`);
  };

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-10 w-72" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-32" />)}
        </div>
      </div>
    );
  }

  const lastUpdate = data.last_rate_update ? new Date(data.last_rate_update).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Head Office</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A] tracking-tight">Dashboard</h1>
        <p className="text-slate-500 mt-2">A premium overview of catalogue, rates and franchise activity.</p>
      </div>

      <form onSubmit={onSearch} className="luxe-card p-2 flex items-center gap-2 max-w-2xl" data-testid="dashboard-search-form">
        <Search className="w-5 h-5 text-slate-400 ml-3" strokeWidth={1.5} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Quick lookup — Jewel Code or Style Number"
          className="border-0 shadow-none focus-visible:ring-0 h-12 text-base bg-transparent"
          data-testid="dashboard-search-input"
        />
        <Button type="submit" className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white h-11" data-testid="dashboard-search-btn">
          Search
        </Button>
      </form>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={Package} label="Total Products" value={formatIndian(data.total_products)} testid="stat-products" />
        <StatCard icon={Store} label="Active Franchises" value={formatIndian(data.total_franchises)} sub={`${formatIndian(data.total_distributors || 0)} Distributors`} testid="stat-franchises" />
        <StatCard icon={TrendingUp} label="Gold Rate (24KT)" value={rupees(data.gold_rate) + "/g"} testid="stat-gold" />
        <StatCard icon={Calendar} label="Last Rate Update" value={lastUpdate} sub={`Version ${data.rate_version}`} testid="stat-rates" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 luxe-card p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C]">Recent</p>
              <h2 className="font-serif text-2xl text-[#0D1B2A] mt-1">Latest uploads</h2>
            </div>
            <Button variant="outline" className="border-[#0D1B2A]/20 text-[#0D1B2A]" onClick={() => navigate("/products")}>
              View all
            </Button>
          </div>
          <div className="gold-divider mb-4" />
          <div className="divide-y divide-[#0D1B2A]/5">
            {data.recent_uploads.length === 0 && (
              <div className="py-12 text-center text-slate-400">
                <Sparkles className="w-8 h-8 mx-auto mb-2 text-[#C9A84C]" strokeWidth={1.5} />
                No products yet — upload your first Excel.
              </div>
            )}
            {data.recent_uploads.map((p) => (
              <button
                key={p.jewel_code}
                onClick={() => navigate(`/products?search=${p.jewel_code}`)}
                data-testid={`recent-${p.jewel_code}`}
                className="w-full text-left py-4 flex items-center justify-between hover:bg-[#FAF8F3] rounded-md px-3 -mx-3 transition-colors"
              >
                <div>
                  <div className="font-medium text-[#0D1B2A]">{p.jewel_code}</div>
                  <div className="text-xs text-slate-500 mt-1">{p.style_no} • {p.category}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${purityColor(p.purity)}`}>{p.purity}</span>
                  <span className="text-xs text-slate-400">{p.source_tag}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="luxe-card p-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C]">Distribution</p>
          <h2 className="font-serif text-2xl text-[#0D1B2A] mt-1 mb-6">Products by source</h2>
          <div className="gold-divider mb-4" />
          <div className="space-y-4">
            {data.source_distribution.map((d) => (
              <div key={d.source_tag}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-[#0D1B2A]">{d.source_tag || "Unassigned"}</span>
                  <span className="tabular-nums text-slate-500">{formatIndian(d.count)}</span>
                </div>
                <div className="h-1 bg-[#0D1B2A]/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#C9A84C]"
                    style={{ width: `${Math.min(100, (d.count / Math.max(1, data.total_products)) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Memo (Consignment) Summary */}
      {memoSummary && (
        <div className="luxe-card p-8" data-testid="memo-summary-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C]">Consignment</p>
              <h2 className="font-serif text-2xl text-[#0D1B2A] mt-1 flex items-center gap-3">
                Memo Goods Summary <MemoBadge />
              </h2>
            </div>
            <Button variant="outline" className="border-[#0D1B2A]/20 text-[#0D1B2A]" onClick={() => navigate("/products?memo=memo")} data-testid="memo-view-all-btn">
              View memo products
            </Button>
          </div>
          <div className="gold-divider mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div data-testid="memo-stat-count">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Memo Products</p>
              <div className="font-serif text-3xl text-[#0D1B2A] tabular-nums mt-1">{formatIndian(memoSummary.total_memo_products)}</div>
            </div>
            <div data-testid="memo-stat-value">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Memo Value (Customer MRP)</p>
              <div className="font-serif text-3xl text-[#C9A84C] tabular-nums mt-1">{rupees(memoSummary.total_memo_value)}</div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Sources with Memo</p>
              <div className="font-serif text-3xl text-[#0D1B2A] tabular-nums mt-1">{formatIndian(memoSummary.by_franchise.length)}</div>
            </div>
          </div>
          {memoSummary.by_franchise.length === 0 ? (
            <div className="text-sm text-slate-400 italic">No memo goods currently tracked. Mark products as MEMO during Excel upload to populate this view.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="memo-by-franchise-table">
                <thead className="text-left">
                  <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-[#0D1B2A]/10">
                    <th className="py-3 pr-4">Source</th>
                    <th className="py-3 pr-4 text-right">Memo Products</th>
                    <th className="py-3 pr-4 text-right">Memo Value (MRP)</th>
                  </tr>
                </thead>
                <tbody>
                  {memoSummary.by_franchise.map((row) => (
                    <tr key={row.source_tag}
                      onClick={() => navigate(`/products?source_tag=${encodeURIComponent(row.source_tag)}&memo=memo`)}
                      data-testid={`memo-row-${row.source_tag}`}
                      className="border-b border-[#0D1B2A]/5 hover:bg-[#FAF8F3] cursor-pointer">
                      <td className="py-3 pr-4 font-medium text-[#0D1B2A]">{row.source_tag}</td>
                      <td className="py-3 pr-4 text-right tabular-nums">{formatIndian(row.count)}</td>
                      <td className="py-3 pr-4 text-right tabular-nums text-[#C9A84C] font-medium">{rupees(row.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="luxe-card p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs uppercase tracking-[0.25em] text-slate-500">{label}</p>
        <Icon className="w-5 h-5 text-[#C9A84C]" strokeWidth={1.5} />
      </div>
      <div className="font-serif text-3xl text-[#0D1B2A] tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}
